import { Button, Field, Input, InputOnChangeData, makeStyles, Text } from "@fluentui/react-components";
import * as React from "react";
import { flexColumn, intro } from "../../styles/GlobalStyles";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGlobalDispatch } from "../../state/GlobalStateProvider";
import { ensureSuccess, handleError } from "../../util/ErrorHandling";
import { CenteredContent } from "../../components/CenteredContent";
import { Id as HowToImportLicenseId } from "../articles/HowToImportLicense";

const useStyles = makeStyles({
    container: {
        ...flexColumn,
        width: "100%"
    },
    intro
});

export const TrialRequestConfirmationPage: React.FC = React.memo(() => {
    const styles = useStyles();
    const { trialRequestId } = useParams();
    const globalDispatch = useGlobalDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [confirmationId, setConfirmationId] = React.useState("");
    const [success, setSuccess] = React.useState(false);
    const [inputLocked, setInputLocked] = React.useState(false);

    const onConfirmationIdChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setConfirmationId(data.value);
    }, []);

    const navigateToDocs = React.useCallback(() =>
    {
        navigate(`/docs/article/customer/${HowToImportLicenseId}`);
    }, [navigate]);

    const submit = React.useCallback(async (confirmationId: string) =>
    {
        try
        {   
            const response = await fetch(`/api/public/trialRequests/${trialRequestId}/confirm?confirmationId=${confirmationId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({})
            });
            
            await ensureSuccess(response);

            setSuccess(true);
        }
        catch (e)
        {
            handleError(e as Error, globalDispatch);
        }
        finally
        {
            setInputLocked(false);
        }
    }, [globalDispatch, trialRequestId]);

    React.useEffect(() =>
    {
        const searchParams = new URLSearchParams(location?.search ?? "");
        const confirmationId = searchParams.get("confirmationId");

        if (confirmationId)
        {
            setConfirmationId(confirmationId);
        }
    }, [location?.search]);

    React.useEffect(() =>
    {
        if (confirmationId.length === 36)
        {
            setInputLocked(true);
            submit(confirmationId);
        }
    }, [confirmationId, submit]);

    if (success)
    {
        return (
            <>
                <div className={styles.container}>
                    <p>
                        <CenteredContent>
                            <Text className={styles.intro}>
                                Success!
                            </Text>
                        </CenteredContent>
                    </p>

                    <CenteredContent>
                        <Text>The license was sent to your mail address!</Text>
                    </CenteredContent>

                    <CenteredContent>
                        <Button onClick={navigateToDocs} appearance="primary">What to do now?</Button>
                    </CenteredContent>
                </div>
            </>
        );
    }
    else
    {
        return (
            <>
                <div className={styles.container}>
                    <p>
                        <CenteredContent>
                            <Text className={styles.intro}>
                                Trial Request Confirmation
                            </Text>
                        </CenteredContent>
                    </p>


                    <p>
                        <CenteredContent>
                            <Text>
                                Please paste the confirmation Id that we sent to you via e-mail below.
                                Can't find it? Take a look in your spam folder, corporate e-mail filters can cause a delay.
                            </Text>
                        </CenteredContent>
                    </p>

                    <p>
                        <CenteredContent>
                            <Field
                                required
                                label="Confirmation Id"
                                hint={`${(confirmationId ?? "").length} / 36`}
                            >
                                <Input value={confirmationId} onChange={onConfirmationIdChange} maxLength={36} disabled={inputLocked} />
                            </Field>
                        </CenteredContent>
                    </p>
                </div>
            </>
        );
    }
});